import Box from '@mui/material/Box';
import { type JSX, useState } from 'react';

import { ClientTPRMApi } from 'Api/TPRM/TPRMApi';
import { ClientUcrmApi } from 'Api/UCRM/UcrmApi';
import { updateAssessmentSubject } from 'Api/UcrmTprm/UcrmTprmApi';
import { Alert } from 'Components/Alert/Alert';
import { Button } from 'Components/Buttons/Buttons';
import { Modal } from 'Components/Modal/Modal';
import { ICON_CLOSE } from 'Config/Icons';
import { iso8601ToJsDate, jsDateToIso8601 } from 'Helpers/DateTimeUtils/DateTimeUtils';
import { ProductModule } from 'Models/ProductModule';
import { ThirdPartyService, UpdateServiceRequest } from 'Models/TPRM';
import { UseCase } from 'Models/Ucrm';

import { IrqTargetCompletionDateSelection, getIrqTargetCompletionDateSelectionProps } from '../IrqTargetCompletionDateSelection/IrqTargetCompletionDateSelection';

interface IrqTargetCompletionDateModalBaseProps {
    hideModal: () => void;
    targetDateSet: () => void;
}

/** `module` determines the types of `moduleApi` and `assessmentSubject`, so that TPRM-only fields are included in the update request based on the module rather than on the shape of `assessmentSubject`. */
export type IrqTargetCompletionDateModalProps = IrqTargetCompletionDateModalBaseProps & ({ module: ProductModule.TPRM; assessmentSubject: ThirdPartyService; moduleApi: ClientTPRMApi } | { module: ProductModule.UCRM; assessmentSubject: UseCase; moduleApi: ClientUcrmApi });

interface FormState {
    isLoading?: boolean;
    successMessage?: string;
    failureMessage?: string;
}

// TODO [TPRM - Service Dashboard]: DRY this with the UI/logic in ServiceDashboard for the start/restart modals, if possible. Lots was copied from there and pasted here.
/**
 * This modal allows the user to set the due date for the Inherent Risk Questionnaire of a specific assessment subject (a TPRM third-party service or a UCRM use case).
 * This is not the modal used to set the default, organization-wide target completion schedules for all inherent risk questionnaires.
 */
export const IrqTargetCompletionDateModal = (props: IrqTargetCompletionDateModalProps): JSX.Element => {
    const [formState, setFormState] = useState<FormState>({});
    const [selectedIrqTargetCompletionDate, setSelectedIrqTargetCompletionDate] = useState<Date | undefined>(props.assessmentSubject.assessment_workflow_setup.irq_target_completion_date ? iso8601ToJsDate(props.assessmentSubject.assessment_workflow_setup.irq_target_completion_date) : undefined);

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault();
        try {
            setFormState({ isLoading: true });
            const request: UpdateServiceRequest = {
                name: props.assessmentSubject.name,
                description: props.assessmentSubject.description,
                vendor_service_manager_user_id: props.assessmentSubject.vendor_service_manager_user_id,
                assessment_due_date: props.assessmentSubject.assessment_workflow_setup.due_date,
                delegates: props.assessmentSubject.delegates,
                irq_target_completion_date: selectedIrqTargetCompletionDate ? jsDateToIso8601(selectedIrqTargetCompletionDate) : undefined,
                tags: props.assessmentSubject.tags,
                // Use cases (UCRM) do not have a responsible organization or contacts.
                ...(props.module === ProductModule.TPRM ? { responsible_organization: props.assessmentSubject.responsible_organization, vendor_contacts: props.assessmentSubject.vendor_contacts } : {}),
            };
            await updateAssessmentSubject(props.moduleApi, request, props.assessmentSubject.vendor_id, props.assessmentSubject.id);
            props.targetDateSet();
            setFormState({ successMessage: 'Inherent risk questionnaire due date saved.', isLoading: false });
        } catch (error) {
            handleRequestError(error);
        }
    };

    const handleRequestError = (error: Error) => {
        setFormState({ failureMessage: error.message, isLoading: false });
    };

    return (
        <Modal open header="Schedule Inherent Risk Questionnaire">
            {formState.successMessage && <Alert type="success">{formState.successMessage}</Alert>}
            {formState.failureMessage && <Alert type="error">{formState.failureMessage}</Alert>}
            <Box component="form" noValidate onSubmit={handleSubmit}>
                <IrqTargetCompletionDateSelection
                    {...getIrqTargetCompletionDateSelectionProps(
                        props.module,
                        {
                            action: 'updateExistingSelection',
                            currentAssessmentWorkflowIrqTargetCompletionDate: props.assessmentSubject.assessment_workflow_setup.irq_target_completion_date,
                        },
                        setSelectedIrqTargetCompletionDate
                    )}
                />
                <div className={'modalFormButtonContainer'}>
                    <Button variant="secondary" onClick={props.hideModal} fontAwesomeImage={ICON_CLOSE}>
                        Close
                    </Button>
                    <Button variant="submit" isLoading={formState.isLoading === true} loadingText="Saving...">
                        Save
                    </Button>
                </div>
            </Box>
        </Modal>
    );
};
